(() => {
  'use strict';

  const ART_DB = 'ops-artwork-scratchpad';
  const ART_STORE = 'uploads';
  const A4_WIDTH_MM = 210;
  const A4_HEIGHT_MM = 297;
  const SHEET_DPI = 300;
  const PX_PER_MM = SHEET_DPI / 25.4;
  const SHEET_MARGIN_MM = 8;
  const SHEET_SPACING_MM = 3.5;
  const DIECUT_BLEED_MM = 1.5;
  const DIECUT_ROUNDED_RADIUS_RATIO = 0.08;
  const DIECUT_ROUNDED_QR_INSET_RATIO = 0.04;
  const EXPORT_SIZE_PX = 1800;
  const PREVIEW_SIZE_PX = 720;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
  const round = (value, decimals = 1) => {
    const factor = 10 ** decimals;
    return Math.round((Number(value) || 0) * factor) / factor;
  };
  const uid = (prefix = 'qr') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function validateDestination(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) return { valid: false, empty: true, value: '', message: '' };
    try {
      const url = new URL(trimmed);
      if (!['https:', 'http:'].includes(url.protocol)) {
        return { valid: false, empty: false, value: trimmed, message: 'Use a web address beginning with https:// or http://' };
      }
      return { valid: true, empty: false, value: url.href, message: 'Destination looks good.' };
    } catch {
      return { valid: false, empty: false, value: trimmed, message: 'Enter a full web address, including https://' };
    }
  }

  function normalizeHex(value) {
    let hex = String(value || '').trim();
    if (!hex) return null;
    if (!hex.startsWith('#')) hex = `#${hex}`;
    if (/^#[0-9a-f]{3}$/i.test(hex)) {
      hex = `#${hex.slice(1).split('').map((character) => character + character).join('')}`;
    }
    return /^#[0-9a-f]{6}$/i.test(hex) ? hex.toUpperCase() : null;
  }

  function hexRgb(hex) {
    const value = normalizeHex(hex);
    if (!value) return [0, 0, 0];
    return [
      parseInt(value.slice(1, 3), 16),
      parseInt(value.slice(3, 5), 16),
      parseInt(value.slice(5, 7), 16),
    ];
  }

  function relativeLuminance(hex) {
    const channels = hexRgb(hex).map((value) => {
      const normal = value / 255;
      return normal <= 0.03928 ? normal / 12.92 : ((normal + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }

  function contrastRatio(first, second) {
    const a = relativeLuminance(first);
    const b = relativeLuminance(second);
    const lighter = Math.max(a, b);
    const darker = Math.min(a, b);
    return (lighter + 0.05) / (darker + 0.05);
  }

  function safeSlug(value) {
    return String(value || 'qr-code')
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 54) || 'qr-code';
  }

  function parseConfig(root) {
    const node = root.querySelector('[data-ops-qr-config]');
    if (!node) return {};
    try { return JSON.parse(node.textContent || '{}') || {}; }
    catch (error) {
      console.warn('OPS QR Maker configuration could not be parsed.', error);
      return {};
    }
  }

  function formatMoney(cents, currency = 'GBP') {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }

  function formatUnitPrice(cents, suffix = 'each', currency = 'GBP') {
    const value = Number(cents || 0);
    if (currency === 'GBP' && value < 100) {
      const digits = value < 10 || value % 1 ? 2 : 0;
      return `${value.toFixed(digits).replace(/\.00$/, '')}p ${suffix}`;
    }
    return `${formatMoney(value, currency)} ${suffix}`;
  }

  function activeDiscount(tiers, quantity) {
    const target = Math.max(1, Math.round(Number(quantity) || 1));
    let active = { quantity: 0, percent: 0 };
    (Array.isArray(tiers) ? tiers : []).forEach((tier) => {
      const threshold = Math.max(1, Math.round(Number(tier?.quantity) || 0));
      const percent = Math.max(0, Number(tier?.percent) || 0);
      if (target >= threshold && percent >= active.percent) active = { quantity: threshold, percent };
    });
    return active;
  }

  function nextDiscount(tiers, quantity) {
    const target = Math.max(1, Math.round(Number(quantity) || 1));
    const current = activeDiscount(tiers, target);
    return (Array.isArray(tiers) ? tiers : [])
      .map((tier) => ({ quantity: Math.max(1, Math.round(Number(tier?.quantity) || 0)), percent: Math.max(0, Number(tier?.percent) || 0) }))
      .filter((tier) => tier.quantity > target && tier.percent > current.percent)
      .sort((a, b) => a.quantity - b.quantity)[0] || null;
  }

  function loadImage(source) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The image could not be loaded.'));
      image.src = source;
    });
  }

  function canvasToBlob(canvas, type = 'image/png', quality) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The browser could not create the output file.')), type, quality);
    });
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1800);
  }

  function roundRectPath(context, x, y, width, height, radius) {
    const r = Math.min(radius, width / 2, height / 2);
    context.beginPath();
    context.moveTo(x + r, y);
    context.lineTo(x + width - r, y);
    context.quadraticCurveTo(x + width, y, x + width, y + r);
    context.lineTo(x + width, y + height - r);
    context.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    context.lineTo(x + r, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - r);
    context.lineTo(x, y + r);
    context.quadraticCurveTo(x, y, x + r, y);
    context.closePath();
  }

  function openArtworkDb() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('Browser storage is not supported on this device.'));
        return;
      }
      const request = indexedDB.open(ART_DB, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(ART_STORE)) {
          request.result.createObjectStore(ART_STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('My Artwork could not be opened.'));
    });
  }

  function putArtwork(record) {
    return openArtworkDb().then((database) => new Promise((resolve, reject) => {
      const transaction = database.transaction(ART_STORE, 'readwrite');
      const request = transaction.objectStore(ART_STORE).put(record);
      transaction.oncomplete = () => resolve(request.result || record.id);
      transaction.onerror = () => reject(transaction.error || request.error || new Error('The QR code could not be saved to My Artwork.'));
    }));
  }

  async function createPdfBlob(canvas) {
    const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', 0.96);
    const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());
    const encoder = new TextEncoder();
    const parts = [];
    const offsets = [0];
    let length = 0;
    const push = (value) => {
      const bytes = typeof value === 'string' ? encoder.encode(value) : value;
      parts.push(bytes);
      length += bytes.length;
    };
    const addObject = (number, bodyParts) => {
      offsets[number] = length;
      push(`${number} 0 obj\n`);
      bodyParts.forEach(push);
      push('\nendobj\n');
    };
    push('%PDF-1.4\n%âãÏÓ\n');
    addObject(1, ['<< /Type /Catalog /Pages 2 0 R >>']);
    addObject(2, ['<< /Type /Pages /Kids [3 0 R] /Count 1 >>']);
    addObject(3, ['<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595.276 841.89] /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>']);
    const content = 'q\n595.276 0 0 841.89 0 0 cm\n/Im0 Do\nQ\n';
    addObject(4, [`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}endstream`]);
    offsets[5] = length;
    push('5 0 obj\n');
    push(`<< /Type /XObject /Subtype /Image /Width ${canvas.width} /Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`);
    push(jpegBytes);
    push('\nendstream\nendobj\n');
    const xrefOffset = length;
    push('xref\n0 6\n');
    push('0000000000 65535 f \n');
    for (let number = 1; number <= 5; number += 1) push(`${String(offsets[number]).padStart(10, '0')} 00000 n \n`);
    push(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);
    const output = new Uint8Array(length);
    let pointer = 0;
    parts.forEach((part) => { output.set(part, pointer); pointer += part.length; });
    return new Blob([output], { type: 'application/pdf' });
  }

  function initQrMaker(root) {
    if (!root || root.dataset.opsQrInitialised === 'true') return;

    const config = parseConfig(root);
    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => [...root.querySelectorAll(selector)];

    const el = {
      destination: q('[data-ops-qr-destination]'),
      destinationStatus: q('[data-ops-qr-destination-status]'),
      preview: q('[data-ops-qr-preview]'),
      logoInput: q('[data-ops-qr-logo]'),
      logoSelected: q('[data-ops-qr-logo-selected]'),
      logoThumb: q('[data-ops-qr-logo-thumb]'),
      logoName: q('[data-ops-qr-logo-name]'),
      logoRemove: q('[data-ops-qr-logo-remove]'),
      logoSize: q('[data-ops-qr-logo-size]'),
      logoSizeValue: q('[data-ops-qr-logo-size-value]'),
      logoSizeWrap: q('[data-ops-qr-logo-size-wrap]'),
      patternButtons: qa('[data-ops-qr-pattern]'),
      cornerButtons: qa('[data-ops-qr-corner]'),
      qrColour: q('[data-ops-qr-colour]'),
      qrColourHex: q('[data-ops-qr-colour-hex]'),
      background: q('[data-ops-qr-background]'),
      backgroundHex: q('[data-ops-qr-background-hex]'),
      safety: q('[data-ops-qr-safety]'),
      actionStatus: q('[data-ops-qr-action-status]'),
      downloadPng: q('[data-ops-qr-download-png]'),
      downloadSvg: q('[data-ops-qr-download-svg]'),
      saveArtwork: q('[data-ops-qr-save-artwork]'),
      sheetPreview: q('[data-ops-qr-sheet-preview]'),
      sheetSize: q('[data-ops-qr-sheet-size]'),
      sheetSizeValue: q('[data-ops-qr-sheet-size-value]'),
      sheetSummary: q('[data-ops-qr-sheet-summary]'),
      sheetFinish: q('[data-ops-qr-sheet-finish]'),
      sheetQuantity: q('[data-ops-qr-sheet-quantity]'),
      sheetMinus: q('[data-ops-qr-sheet-minus]'),
      sheetPlus: q('[data-ops-qr-sheet-plus]'),
      sheetPresets: q('[data-ops-qr-sheet-presets]'),
      sheetPrice: q('[data-ops-qr-sheet-price]'),
      sheetUnit: q('[data-ops-qr-sheet-unit]'),
      sheetSaving: q('[data-ops-qr-sheet-saving]'),
      sheetProof: q('[data-ops-qr-sheet-proof]'),
      sheetAdd: q('[data-ops-qr-sheet-add]'),
      sheetDownload: q('[data-ops-qr-sheet-download]'),
      sheetAdvanced: q('[data-ops-qr-sheet-advanced]'),
      sheetStatus: q('[data-ops-qr-sheet-status]'),
      diecutPreview: q('[data-ops-qr-diecut-preview]'),
      diecutEmpty: q('[data-ops-qr-diecut-empty]'),
      diecutSize: q('[data-ops-qr-diecut-size]'),
      diecutSizeValue: q('[data-ops-qr-diecut-size-value]'),
      diecutSizePresets: q('[data-ops-qr-diecut-size-presets]'),
      diecutShape: q('[data-ops-qr-diecut-shape]'),
      diecutShapeValue: q('[data-ops-qr-diecut-shape-value]'),
      diecutFinish: q('[data-ops-qr-diecut-finish]'),
      diecutQuantity: q('[data-ops-qr-diecut-quantity]'),
      diecutMinus: q('[data-ops-qr-diecut-minus]'),
      diecutPlus: q('[data-ops-qr-diecut-plus]'),
      diecutPresets: q('[data-ops-qr-diecut-presets]'),
      diecutPrice: q('[data-ops-qr-diecut-price]'),
      diecutUnit: q('[data-ops-qr-diecut-unit]'),
      diecutSaving: q('[data-ops-qr-diecut-saving]'),
      diecutProof: q('[data-ops-qr-diecut-proof]'),
      diecutAdd: q('[data-ops-qr-diecut-add]'),
      diecutAdvanced: q('[data-ops-qr-diecut-advanced]'),
      diecutStatus: q('[data-ops-qr-diecut-status]'),
    };

    if (!el.destination || !el.destinationStatus || !el.preview) return;
    root.dataset.opsQrInitialised = 'true';

    const state = {
      destination: '',
      logoDataUrl: '',
      pattern: 'square',
      corner: 'square',
      qrColour: normalizeHex(el.qrColour?.value) || '#000000',
      background: normalizeHex(el.background?.value) || '#FFFFFF',
      sheetSizeMm: Number(el.sheetSize?.value) || 36,
      sheetQuantity: Number(el.sheetQuantity?.value) || 1,
      diecutSizeMm: Number(el.diecutSize?.value) || 50,
      diecutShape: 'square',
      diecutQuantity: Number(el.diecutQuantity?.value) || 25,
      sheetProduct: config.sheetProduct || null,
      diecutProduct: config.diecutProduct || null,
      workerUrl: String(config.workerUrl || 'https://occult-print-upload.mwsb87.workers.dev').replace(/\/+$/, ''),
      discountTiers: Array.isArray(config.discountTiers) ? config.discountTiers : [],
      contrast: 21,
      qrValid: false,
      busy: false,
      previewRevision: 0,
      sheetVariant: null,
      diecutPlan: null,
      diecutUsesPackVariants: false,
      diecutOptionIndexes: null,
    };

    function status(node, message = '', type = '') {
      if (!node) return;
      node.textContent = message;
      if (type) node.dataset.state = type;
      else node.removeAttribute('data-state');
    }

    function setBusy(button, busy, busyLabel = '') {
      if (!button) return;
      if (busy) {
        button.dataset.previousLabel = button.textContent;
        button.dataset.busy = 'true';
        if (busyLabel) button.textContent = busyLabel;
      } else {
        if (button.dataset.previousLabel) button.textContent = button.dataset.previousLabel;
        delete button.dataset.previousLabel;
        button.removeAttribute('data-busy');
      }
      button.disabled = busy || button.disabled;
    }

    function getCornerOptions() {
      if (state.corner === 'rounded') return { outer: 'extra-rounded', inner: 'dot' };
      if (state.corner === 'dot') return { outer: 'dot', inner: 'dot' };
      return { outer: 'square', inner: 'square' };
    }

    function qrOptions(size, destination = state.destination, type = 'canvas') {
      const corners = getCornerOptions();
      return {
        width: size,
        height: size,
        type,
        data: destination,
        margin: Math.round(size * (24 / 720)),
        qrOptions: { errorCorrectionLevel: 'H' },
        dotsOptions: { color: state.qrColour, type: state.pattern },
        cornersSquareOptions: { color: state.qrColour, type: corners.outer },
        cornersDotOptions: { color: state.qrColour, type: corners.inner },
        backgroundOptions: { color: state.background },
      };
    }

    function showEmptyPreview(message) {
      el.preview.innerHTML = '';
      const empty = document.createElement('span');
      empty.dataset.opsQrPreviewEmpty = '';
      empty.textContent = message;
      el.preview.appendChild(empty);
    }

    function addLiveLogoOverlay() {
      if (!state.logoDataUrl) return;
      const overlay = document.createElement('div');
      overlay.className = 'ops-qr-maker__logo-overlay';
      overlay.style.width = `${Number(el.logoSize?.value) || 20}%`;
      overlay.style.backgroundColor = state.background;
      const image = document.createElement('img');
      image.src = state.logoDataUrl;
      image.alt = '';
      overlay.appendChild(image);
      el.preview.appendChild(overlay);
    }

    function renderLiveQr() {
      if (!state.qrValid) return;
      if (typeof QRCodeStyling === 'undefined') {
        showEmptyPreview('QR engine could not be loaded.');
        return;
      }
      el.preview.innerHTML = '';
      const qr = new QRCodeStyling(qrOptions(PREVIEW_SIZE_PX));
      qr.append(el.preview);
      addLiveLogoOverlay();
      const revision = ++state.previewRevision;
      requestAnimationFrame(() => updateDerivedPreviews(revision));
      setTimeout(() => updateDerivedPreviews(revision), 45);
    }

    async function compositeCanvas(baseCanvas, outputSize = baseCanvas?.width || PREVIEW_SIZE_PX) {
      if (!baseCanvas) throw new Error('The QR preview is not ready yet.');
      const output = document.createElement('canvas');
      output.width = outputSize;
      output.height = outputSize;
      const context = output.getContext('2d', { alpha: false });
      context.imageSmoothingEnabled = false;
      context.drawImage(baseCanvas, 0, 0, outputSize, outputSize);
      if (!state.logoDataUrl) return output;

      const logo = await loadImage(state.logoDataUrl);
      const patchSize = outputSize * ((Number(el.logoSize?.value) || 20) / 100);
      const patchX = (outputSize - patchSize) / 2;
      const patchY = patchX;
      context.fillStyle = state.background;
      roundRectPath(context, patchX, patchY, patchSize, patchSize, patchSize * 0.12);
      context.fill();

      const inner = patchSize * 0.8;
      const aspect = (logo.naturalWidth || logo.width || 1) / Math.max(1, logo.naturalHeight || logo.height || 1);
      let width = inner;
      let height = inner;
      if (aspect > 1) height = width / aspect;
      else width = height * aspect;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(logo, (outputSize - width) / 2, (outputSize - height) / 2, width, height);
      return output;
    }

    async function currentCompositeCanvas() {
      const base = el.preview.querySelector('canvas');
      return compositeCanvas(base, base?.width || PREVIEW_SIZE_PX);
    }

    async function renderCompositeQrCanvas(size = EXPORT_SIZE_PX) {
      if (!state.qrValid) throw new Error('Enter a valid destination first.');
      if (typeof QRCodeStyling === 'undefined') throw new Error('QR engine could not be loaded.');
      const host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden;';
      document.body.appendChild(host);
      try {
        const qr = new QRCodeStyling(qrOptions(size));
        qr.append(host);
        let canvas = null;
        for (let attempt = 0; attempt < 20; attempt += 1) {
          canvas = host.querySelector('canvas');
          if (canvas && canvas.width > 0) break;
          await delay(16);
        }
        if (!canvas) throw new Error('The QR code could not be rendered.');
        await delay(25);
        return await compositeCanvas(canvas, size);
      } finally {
        host.remove();
      }
    }

    async function renderQrSvgBlob() {
      if (!state.qrValid) throw new Error('Enter a valid destination first.');
      const host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden;';
      document.body.appendChild(host);
      try {
        const qr = new QRCodeStyling(qrOptions(1200, state.destination, 'svg'));
        qr.append(host);
        let svg = null;
        for (let attempt = 0; attempt < 20; attempt += 1) {
          svg = host.querySelector('svg');
          if (svg) break;
          await delay(16);
        }
        if (!svg) throw new Error('The SVG could not be created.');
        const clone = svg.cloneNode(true);
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clone.setAttribute('width', '1200');
        clone.setAttribute('height', '1200');
        clone.setAttribute('viewBox', '0 0 1200 1200');

        if (state.logoDataUrl) {
          const ns = 'http://www.w3.org/2000/svg';
          const patchSize = 1200 * ((Number(el.logoSize?.value) || 20) / 100);
          const patchX = (1200 - patchSize) / 2;
          const rect = document.createElementNS(ns, 'rect');
          rect.setAttribute('x', String(patchX));
          rect.setAttribute('y', String(patchX));
          rect.setAttribute('width', String(patchSize));
          rect.setAttribute('height', String(patchSize));
          rect.setAttribute('rx', String(patchSize * 0.12));
          rect.setAttribute('fill', state.background);
          clone.appendChild(rect);

          const logo = await loadImage(state.logoDataUrl);
          const inner = patchSize * 0.8;
          const aspect = (logo.naturalWidth || logo.width || 1) / Math.max(1, logo.naturalHeight || logo.height || 1);
          let width = inner;
          let height = inner;
          if (aspect > 1) height = width / aspect;
          else width = height * aspect;
          const image = document.createElementNS(ns, 'image');
          image.setAttribute('href', state.logoDataUrl);
          image.setAttribute('x', String((1200 - width) / 2));
          image.setAttribute('y', String((1200 - height) / 2));
          image.setAttribute('width', String(width));
          image.setAttribute('height', String(height));
          image.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          clone.appendChild(image);
        }

        const xml = `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(clone)}`;
        return new Blob([xml], { type: 'image/svg+xml' });
      } finally {
        host.remove();
      }
    }

    function calculateSheetLayout(sizeMm = state.sheetSizeMm) {
      const size = clamp(sizeMm, 20, 60);
      const usableWidth = A4_WIDTH_MM - SHEET_MARGIN_MM * 2;
      const usableHeight = A4_HEIGHT_MM - SHEET_MARGIN_MM * 2;
      const columns = Math.max(1, Math.floor((usableWidth + SHEET_SPACING_MM) / (size + SHEET_SPACING_MM)));
      const rows = Math.max(1, Math.floor((usableHeight + SHEET_SPACING_MM) / (size + SHEET_SPACING_MM)));
      const usedWidth = columns * size + Math.max(0, columns - 1) * SHEET_SPACING_MM;
      const usedHeight = rows * size + Math.max(0, rows - 1) * SHEET_SPACING_MM;
      const startX = (A4_WIDTH_MM - usedWidth) / 2;
      const startY = (A4_HEIGHT_MM - usedHeight) / 2;
      const cells = [];
      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          cells.push({
            x: startX + column * (size + SHEET_SPACING_MM),
            y: startY + row * (size + SHEET_SPACING_MM),
            width: size,
            height: size,
          });
        }
      }
      return { size, columns, rows, count: cells.length, cells, margin: SHEET_MARGIN_MM, spacing: SHEET_SPACING_MM };
    }

    async function drawSheetPreview() {
      if (!el.sheetPreview) return;
      const context = el.sheetPreview.getContext('2d');
      context.clearRect(0, 0, el.sheetPreview.width, el.sheetPreview.height);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, el.sheetPreview.width, el.sheetPreview.height);
      if (!state.qrValid) return;
      const qr = await currentCompositeCanvas().catch(() => null);
      if (!qr) return;
      const layout = calculateSheetLayout();
      const scaleX = el.sheetPreview.width / A4_WIDTH_MM;
      const scaleY = el.sheetPreview.height / A4_HEIGHT_MM;
      context.imageSmoothingEnabled = false;
      layout.cells.forEach((cell) => {
        context.drawImage(qr, cell.x * scaleX, cell.y * scaleY, cell.width * scaleX, cell.height * scaleY);
      });
      context.strokeStyle = 'rgba(0,0,0,.12)';
      context.lineWidth = 1;
      layout.cells.forEach((cell) => {
        context.strokeRect(cell.x * scaleX, cell.y * scaleY, cell.width * scaleX, cell.height * scaleY);
      });
    }

    async function createSheetCanvas() {
      const qr = await renderCompositeQrCanvas(EXPORT_SIZE_PX);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(A4_WIDTH_MM * PX_PER_MM);
      canvas.height = Math.round(A4_HEIGHT_MM * PX_PER_MM);
      const context = canvas.getContext('2d', { alpha: false });
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = false;
      const layout = calculateSheetLayout();
      layout.cells.forEach((cell) => {
        context.drawImage(
          qr,
          Math.round(cell.x * PX_PER_MM),
          Math.round(cell.y * PX_PER_MM),
          Math.round(cell.width * PX_PER_MM),
          Math.round(cell.height * PX_PER_MM)
        );
      });
      return { canvas, layout };
    }

    async function createSheetPreviewCanvas() {
      const qr = await renderCompositeQrCanvas(900);
      const canvas = document.createElement('canvas');
      canvas.width = 840;
      canvas.height = 1188;
      const context = canvas.getContext('2d', { alpha: false });
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = false;
      const layout = calculateSheetLayout();
      const scaleX = canvas.width / A4_WIDTH_MM;
      const scaleY = canvas.height / A4_HEIGHT_MM;
      layout.cells.forEach((cell) => {
        context.drawImage(qr, cell.x * scaleX, cell.y * scaleY, cell.width * scaleX, cell.height * scaleY);
      });
      return canvas;
    }

    function createSheetCutSvg(layout = calculateSheetLayout()) {
      const lines = [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<svg xmlns="http://www.w3.org/2000/svg" width="${A4_WIDTH_MM}mm" height="${A4_HEIGHT_MM}mm" viewBox="0 0 ${A4_WIDTH_MM} ${A4_HEIGHT_MM}">`,
        '<title>QR sticker sheet cut path</title>',
        '<g fill="none" stroke="#ff00ff" stroke-width="0.2">',
      ];
      layout.cells.forEach((cell) => {
        lines.push(`<rect x="${round(cell.x, 3)}" y="${round(cell.y, 3)}" width="${round(cell.width, 3)}" height="${round(cell.height, 3)}"/>`);
      });
      lines.push('</g>', '</svg>');
      return lines.join('\n');
    }

    function diecutRounded() {
      return state.diecutShape === 'rounded';
    }

    function diecutCornerRadiusMm(sizeMm = state.diecutSizeMm) {
      return diecutRounded() ? Math.max(1.5, Number(sizeMm) * DIECUT_ROUNDED_RADIUS_RATIO) : 0;
    }

    function diecutQrInsetMm(sizeMm = state.diecutSizeMm) {
      return diecutRounded() ? Math.max(1, Number(sizeMm) * DIECUT_ROUNDED_QR_INSET_RATIO) : 0;
    }

    async function createDiecutDisplayCanvas(sizePx = 900) {
      const canvas = document.createElement('canvas');
      canvas.width = sizePx;
      canvas.height = sizePx;
      const context = canvas.getContext('2d', { alpha: false });
      context.fillStyle = state.background;
      context.fillRect(0, 0, sizePx, sizePx);
      const qr = await renderCompositeQrCanvas(sizePx);
      const insetPx = diecutRounded() ? Math.round(sizePx * DIECUT_ROUNDED_QR_INSET_RATIO) : 0;
      context.imageSmoothingEnabled = false;
      context.drawImage(qr, insetPx, insetPx, sizePx - insetPx * 2, sizePx - insetPx * 2);
      return canvas;
    }

    async function createDiecutPrintCanvas() {
      const sizeMm = clamp(state.diecutSizeMm, 20, 100);
      const totalMm = sizeMm + DIECUT_BLEED_MM * 2;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(totalMm * PX_PER_MM);
      canvas.height = Math.round(totalMm * PX_PER_MM);
      const context = canvas.getContext('2d', { alpha: false });
      context.fillStyle = state.background;
      context.fillRect(0, 0, canvas.width, canvas.height);
      const qr = await renderCompositeQrCanvas(EXPORT_SIZE_PX);
      const bleedPx = Math.round(DIECUT_BLEED_MM * PX_PER_MM);
      const sizePx = Math.round(sizeMm * PX_PER_MM);
      const insetPx = Math.round(diecutQrInsetMm(sizeMm) * PX_PER_MM);
      context.imageSmoothingEnabled = false;
      context.drawImage(qr, bleedPx + insetPx, bleedPx + insetPx, sizePx - insetPx * 2, sizePx - insetPx * 2);
      return canvas;
    }

    async function createDiecutProofCanvas() {
      const sizeMm = clamp(state.diecutSizeMm, 20, 100);
      const totalMm = sizeMm + DIECUT_BLEED_MM * 2;
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 1200;
      const context = canvas.getContext('2d', { alpha: false });
      context.fillStyle = state.background;
      context.fillRect(0, 0, canvas.width, canvas.height);
      const qr = await renderCompositeQrCanvas(1100);
      const bleedPx = canvas.width * (DIECUT_BLEED_MM / totalMm);
      const sizePx = canvas.width * (sizeMm / totalMm);
      const insetPx = canvas.width * (diecutQrInsetMm(sizeMm) / totalMm);
      context.imageSmoothingEnabled = false;
      context.drawImage(qr, bleedPx + insetPx, bleedPx + insetPx, sizePx - insetPx * 2, sizePx - insetPx * 2);
      context.strokeStyle = '#ff00ff';
      context.lineWidth = Math.max(2, canvas.width / 500);
      if (diecutRounded()) {
        const radiusPx = canvas.width * (diecutCornerRadiusMm(sizeMm) / totalMm);
        roundRectPath(context, bleedPx, bleedPx, sizePx, sizePx, radiusPx);
        context.stroke();
      } else {
        context.strokeRect(bleedPx, bleedPx, sizePx, sizePx);
      }
      return canvas;
    }

    function createDiecutCutSvg() {
      const sizeMm = clamp(state.diecutSizeMm, 20, 100);
      const totalMm = sizeMm + DIECUT_BLEED_MM * 2;
      const radiusMm = diecutCornerRadiusMm(sizeMm);
      const radiusAttributes = radiusMm ? ` rx="${round(radiusMm, 3)}" ry="${round(radiusMm, 3)}"` : '';
      return [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<svg xmlns="http://www.w3.org/2000/svg" width="${totalMm}mm" height="${totalMm}mm" viewBox="0 0 ${totalMm} ${totalMm}">`,
        `<title>QR sticker ${diecutRounded() ? 'rounded-corner' : 'square'} cut path</title>`,
        `<rect x="${DIECUT_BLEED_MM}" y="${DIECUT_BLEED_MM}" width="${sizeMm}" height="${sizeMm}"${radiusAttributes} fill="none" stroke="#ff00ff" stroke-width="0.2"/>`,
        '</svg>',
      ].join('\n');
    }

    function optionNames(product) {
      return (Array.isArray(product?.options) ? product.options : []).map((option) => String(typeof option === 'string' ? option : option?.name || ''));
    }

    function variantValues(variant) {
      return (Array.isArray(variant?.options) ? variant.options : [variant?.option1, variant?.option2, variant?.option3])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    }

    function isLegacySheetVariant(variant) {
      return variantValues(variant).some((value) => /(?:standard\s*print|unlaminated|plain\s*sticker)/i.test(value));
    }

    function sheetUnits(variant) {
      const value = variantValues(variant).find((entry) => /\b(?:sheet|sheets|pack|quantity)\b/i.test(entry) && /\d+/.test(entry));
      const match = value?.match(/(\d+)/);
      return match ? Math.max(1, Number(match[1])) : 1;
    }

    function finishDisplay(value) {
      const label = String(value || '').trim();
      if (/gloss/i.test(label)) return 'Gloss Laminated';
      if (/matte/i.test(label)) return 'Matte Laminated';
      return label || 'Gloss Laminated';
    }

    function sheetFinish(variant) {
      const values = variantValues(variant).filter((value) => !/^default title$/i.test(value));
      const finish = values.find((value) => !(/\b(?:sheet|sheets|pack|quantity)\b/i.test(value) && /\d+/.test(value)));
      return finishDisplay(finish || values[0] || 'Gloss Laminated');
    }

    function sheetSingleVariants() {
      const variants = (state.sheetProduct?.variants || []).filter((variant) => variant.available !== false && !isLegacySheetVariant(variant));
      const singles = variants.filter((variant) => sheetUnits(variant) === 1);
      return singles.length ? singles : variants;
    }

    function populateSheetFinish() {
      if (!el.sheetFinish) return;
      const variants = sheetSingleVariants();
      const finishes = [];
      variants.forEach((variant) => {
        const finish = sheetFinish(variant);
        if (finish && !finishes.includes(finish)) finishes.push(finish);
      });
      el.sheetFinish.innerHTML = '';
      if (!finishes.length) {
        el.sheetFinish.add(new Option('Ordering unavailable', ''));
        el.sheetFinish.disabled = true;
        return;
      }
      finishes.forEach((finish) => el.sheetFinish.add(new Option(finish, finish)));
      const gloss = finishes.find((finish) => /gloss/i.test(finish));
      if (gloss) el.sheetFinish.value = gloss;
      el.sheetFinish.disabled = false;
      updateSheetPricing();
    }

    function selectedSheetVariant() {
      const wanted = String(el.sheetFinish?.value || '');
      return sheetSingleVariants().find((variant) => sheetFinish(variant) === wanted) || sheetSingleVariants()[0] || null;
    }

    function updateSheetPricing() {
      state.sheetQuantity = clamp(Math.round(Number(el.sheetQuantity?.value) || 1), 1, 1000);
      if (el.sheetQuantity) el.sheetQuantity.value = String(state.sheetQuantity);
      const variant = selectedSheetVariant();
      state.sheetVariant = variant;
      const base = Number(variant?.price || 0);
      const subtotal = base * state.sheetQuantity;
      const discount = activeDiscount(state.discountTiers, state.sheetQuantity);
      const saving = Math.round(subtotal * discount.percent / 100);
      const total = Math.max(0, subtotal - saving);
      if (el.sheetPrice) el.sheetPrice.textContent = variant ? `${discount.percent ? 'Estimated ' : ''}${formatMoney(total, config.currency)}` : 'Choose a finish';
      if (el.sheetUnit) el.sheetUnit.textContent = variant ? formatUnitPrice(total / state.sheetQuantity, 'per A4 sheet', config.currency) : '';
      if (el.sheetSaving) {
        const next = nextDiscount(state.discountTiers, state.sheetQuantity);
        el.sheetSaving.textContent = discount.percent ? `${discount.percent}% saving · ${formatMoney(saving, config.currency)} off` : next ? `Savings start at ${next.quantity} sheets` : '';
      }
      el.sheetPresets?.querySelectorAll('[data-quantity]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.quantity) === state.sheetQuantity));
      updateActionAvailability();
    }

    function setupDiecutProduct() {
      const product = state.diecutProduct;
      if (!product || !Array.isArray(product.variants) || !product.variants.length) {
        if (el.diecutFinish) {
          el.diecutFinish.innerHTML = '<option>Ordering unavailable</option>';
          el.diecutFinish.disabled = true;
        }
        return;
      }
      const names = optionNames(product);
      state.diecutOptionIndexes = {
        size: names.findIndex((name) => /size/i.test(name)),
        finish: names.findIndex((name) => /finish/i.test(name)),
        quantity: names.findIndex((name) => /quantity|pack/i.test(name)),
      };
      state.diecutUsesPackVariants = state.diecutOptionIndexes.quantity >= 0;
      const finishIndex = state.diecutOptionIndexes.finish;
      const finishes = [];
      product.variants.forEach((variant) => {
        if (variant.available === false) return;
        const value = finishIndex >= 0 ? String(variant.options?.[finishIndex] || '') : 'Gloss';
        if (value && !finishes.includes(value)) finishes.push(value);
      });
      if (el.diecutFinish) {
        el.diecutFinish.innerHTML = '';
        finishes.forEach((finish) => el.diecutFinish.add(new Option(finish, finish)));
        const gloss = finishes.find((finish) => /gloss/i.test(finish));
        if (gloss) el.diecutFinish.value = gloss;
        el.diecutFinish.disabled = !finishes.length;
      }
      updateDiecutPricing();
    }

    function diecutSizeCategory() {
      const size = Number(state.diecutSizeMm) || 50;
      if (size <= 60) return 'Small';
      if (size <= 90) return 'Medium';
      if (size <= 130) return 'Large';
      return 'X Large';
    }

    function matchingDiecutSizeOption() {
      const product = state.diecutProduct;
      const index = state.diecutOptionIndexes?.size ?? -1;
      if (index < 0 || !product) return '';
      const key = diecutSizeCategory().toLowerCase();
      const values = [];
      product.variants.forEach((variant) => {
        const value = String(variant.options?.[index] ?? '').trim();
        if (value && !values.includes(value)) values.push(value);
      });
      return values.find((value) => value.toLowerCase().startsWith(key)) || '';
    }

    function diecutPackUnits(variant) {
      const index = state.diecutOptionIndexes?.quantity ?? -1;
      if (index < 0) return 1;
      const value = String(variant?.options?.[index] ?? '').trim();
      if (/sample|single/i.test(value)) return 1;
      const match = value.match(/(\d+)/);
      return match ? Math.max(1, Number(match[1])) : 1;
    }

    function diecutPackLabel(variant) {
      const index = state.diecutOptionIndexes?.quantity ?? -1;
      return index >= 0 ? String(variant?.options?.[index] ?? '').trim() : 'Single';
    }

    function diecutVariantsForOrder() {
      const product = state.diecutProduct;
      if (!product || !Array.isArray(product.variants)) return [];
      const indexes = state.diecutOptionIndexes || {};
      const wantedSize = indexes.size >= 0 ? matchingDiecutSizeOption() : '';
      const wantedFinish = indexes.finish >= 0 ? String(el.diecutFinish?.value || '') : '';
      const byUnits = new Map();
      product.variants.forEach((variant) => {
        if (variant.available === false) return;
        if (indexes.size >= 0 && wantedSize && String(variant.options?.[indexes.size] ?? '') !== wantedSize) return;
        if (indexes.finish >= 0 && wantedFinish && String(variant.options?.[indexes.finish] ?? '') !== wantedFinish) return;
        const units = diecutPackUnits(variant);
        const current = byUnits.get(units);
        if (!current || Number(variant.price) < Number(current.price)) byUnits.set(units, variant);
      });
      return [...byUnits.entries()]
        .map(([units, variant]) => ({ units: Number(units), variant, price: Number(variant.price || 0) }))
        .filter((item) => item.units > 0 && item.price >= 0)
        .sort((a, b) => b.units - a.units);
    }

    function buildDiecutPlan(quantity = state.diecutQuantity) {
      const target = clamp(Math.round(Number(quantity) || 1), 1, 1000);
      const packs = diecutVariantsForOrder();
      if (!packs.length) return null;
      const dp = Array(target + 1).fill(null);
      dp[0] = { cost: 0, packCount: 0, prev: -1, choice: null };
      for (let amount = 1; amount <= target; amount += 1) {
        packs.forEach((pack) => {
          if (pack.units > amount || !dp[amount - pack.units]) return;
          const previous = dp[amount - pack.units];
          const candidateCost = previous.cost + pack.price;
          const candidatePacks = previous.packCount + 1;
          const current = dp[amount];
          if (!current || candidateCost < current.cost || (candidateCost === current.cost && candidatePacks < current.packCount)) {
            dp[amount] = { cost: candidateCost, packCount: candidatePacks, prev: amount - pack.units, choice: pack };
          }
        });
      }
      if (!dp[target]) return null;
      const grouped = new Map();
      let cursor = target;
      while (cursor > 0) {
        const step = dp[cursor];
        if (!step?.choice) return null;
        const key = String(step.choice.variant.id);
        const item = grouped.get(key) || { ...step.choice, packs: 0 };
        item.packs += 1;
        grouped.set(key, item);
        cursor = step.prev;
      }
      return { quantity: target, total: dp[target].cost, items: [...grouped.values()].sort((a, b) => b.units - a.units) };
    }

    function diecutPlanLabel(plan = state.diecutPlan) {
      if (!plan?.items?.length) return '';
      return plan.items.map((item) => `${item.packs} × ${diecutPackLabel(item.variant) || `${item.units} stickers`}`).join(' + ');
    }

    function updateDiecutPricing() {
      state.diecutQuantity = clamp(Math.round(Number(el.diecutQuantity?.value) || 25), 1, 1000);
      if (el.diecutQuantity) el.diecutQuantity.value = String(state.diecutQuantity);
      const plan = buildDiecutPlan(state.diecutQuantity);
      state.diecutPlan = plan;
      let total = Number(plan?.total || 0);
      let saving = { cents: 0, percent: 0 };
      if (plan && !state.diecutUsesPackVariants && state.diecutProduct?.handle !== 'custom-stickers') {
        const discount = activeDiscount(state.discountTiers, state.diecutQuantity);
        saving = { percent: discount.percent, cents: Math.round(total * discount.percent / 100) };
        total = Math.max(0, total - saving.cents);
      }
      if (el.diecutPrice) el.diecutPrice.textContent = plan ? `${saving.percent ? 'Estimated ' : ''}${formatMoney(total, config.currency)}` : 'No matching option';
      if (el.diecutUnit) el.diecutUnit.textContent = plan ? formatUnitPrice(total / state.diecutQuantity, 'each', config.currency) : '';
      if (el.diecutSaving) {
        if (state.diecutUsesPackVariants) el.diecutSaving.textContent = plan ? `Best bundle mix: ${diecutPlanLabel(plan)}` : '';
        else {
          const next = state.diecutProduct?.handle === 'custom-stickers' ? null : nextDiscount(state.discountTiers, state.diecutQuantity);
          el.diecutSaving.textContent = saving.percent ? `${saving.percent}% saving · ${formatMoney(saving.cents, config.currency)} off` : next ? `Savings start at ${next.quantity} stickers` : '';
        }
      }
      el.diecutPresets?.querySelectorAll('[data-quantity]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.quantity) === state.diecutQuantity));
      el.diecutSizePresets?.querySelectorAll('[data-size]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.size) === state.diecutSizeMm));
      updateActionAvailability();
    }

    function updateSafety() {
      state.contrast = contrastRatio(state.qrColour, state.background);
      if (!el.safety) return;
      if (state.contrast >= 4.5) {
        el.safety.dataset.state = 'good';
        el.safety.textContent = `Strong scan contrast · ${state.contrast.toFixed(1)}:1`;
      } else if (state.contrast >= 3) {
        el.safety.dataset.state = 'warn';
        el.safety.textContent = `Moderate contrast · ${state.contrast.toFixed(1)}:1. Test the code before printing large quantities.`;
      } else {
        el.safety.dataset.state = 'bad';
        el.safety.textContent = `Low contrast · ${state.contrast.toFixed(1)}:1. Increase the difference between the QR and background before ordering.`;
      }
    }

    function updateActionAvailability() {
      const usable = state.qrValid;
      const orderSafe = usable && state.contrast >= 3;
      if (el.downloadPng) el.downloadPng.disabled = !usable || state.busy;
      if (el.downloadSvg) el.downloadSvg.disabled = !usable || state.busy;
      if (el.saveArtwork) el.saveArtwork.disabled = !usable || state.busy;
      if (el.sheetDownload) el.sheetDownload.disabled = !usable || state.busy;
      if (el.sheetAdvanced) el.sheetAdvanced.disabled = !usable || state.busy;
      if (el.diecutAdvanced) el.diecutAdvanced.disabled = !usable || state.busy;
      if (el.sheetAdd) el.sheetAdd.disabled = !orderSafe || !state.sheetVariant || !el.sheetProof?.checked || state.busy;
      if (el.diecutAdd) el.diecutAdd.disabled = !orderSafe || !state.diecutPlan?.items?.length || !el.diecutProof?.checked || state.busy;
    }

    function updateMaker() {
      const result = validateDestination(el.destination.value);
      el.destinationStatus.textContent = result.message;
      state.qrValid = result.valid;
      state.destination = result.valid ? result.value : '';

      if (result.empty) {
        el.destination.removeAttribute('aria-invalid');
        el.destinationStatus.removeAttribute('data-state');
        showEmptyPreview('Enter a destination to create your QR code.');
      } else if (!result.valid) {
        el.destination.setAttribute('aria-invalid', 'true');
        el.destinationStatus.dataset.state = 'invalid';
        showEmptyPreview('Enter a valid destination to preview your QR code.');
      } else {
        el.destination.removeAttribute('aria-invalid');
        el.destinationStatus.dataset.state = 'valid';
        renderLiveQr();
      }
      updateSafety();
      updateActionAvailability();
      if (!state.qrValid) {
        drawSheetPreview().catch(() => {});
        if (el.diecutPreview) el.diecutPreview.hidden = true;
        if (el.diecutEmpty) el.diecutEmpty.hidden = false;
      }
    }

    async function updateDerivedPreviews(revision) {
      if (revision !== state.previewRevision || !state.qrValid) return;
      await drawSheetPreview().catch(() => {});
      if (revision !== state.previewRevision) return;
      try {
        const stickerPreview = await createDiecutDisplayCanvas();
        if (revision !== state.previewRevision) return;
        if (el.diecutPreview) {
          el.diecutPreview.src = stickerPreview.toDataURL('image/png');
          el.diecutPreview.style.borderRadius = diecutRounded() ? '8%' : '0';
          el.diecutPreview.hidden = false;
        }
        if (el.diecutEmpty) el.diecutEmpty.hidden = true;
      } catch {}
    }

    function setPressedButton(buttons, selectedButton) {
      buttons.forEach((button) => button.setAttribute('aria-pressed', button === selectedButton ? 'true' : 'false'));
    }

    function bindColourControl(picker, hexInput, setter) {
      if (!picker || !hexInput) return;
      picker.addEventListener('input', () => {
        const colour = normalizeHex(picker.value);
        if (!colour) return;
        hexInput.value = colour;
        setter(colour);
        updateMaker();
      });
      hexInput.addEventListener('input', () => {
        const colour = normalizeHex(hexInput.value);
        if (!colour) return;
        picker.value = colour;
        setter(colour);
        updateMaker();
      });
      hexInput.addEventListener('blur', () => {
        const colour = normalizeHex(hexInput.value);
        if (colour) {
          hexInput.value = colour;
          picker.value = colour;
          setter(colour);
          updateMaker();
        } else hexInput.value = picker.value.toUpperCase();
      });
    }

    function clearLogo() {
      state.logoDataUrl = '';
      if (el.logoInput) el.logoInput.value = '';
      if (el.logoSelected) el.logoSelected.hidden = true;
      if (el.logoThumb) el.logoThumb.innerHTML = '';
      if (el.logoName) el.logoName.textContent = '';
      if (el.logoSizeWrap) el.logoSizeWrap.hidden = true;
      updateMaker();
    }

    function handleLogoUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
      const extensionAllowed = /\.(png|jpe?g|webp|svg)$/i.test(file.name);
      if (!allowedTypes.includes(file.type) && !extensionAllowed) {
        window.alert('Please upload a PNG, JPG, WebP or SVG image.');
        clearLogo();
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        window.alert('Please use a logo smaller than 5 MB.');
        clearLogo();
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        state.logoDataUrl = String(reader.result || '');
        if (el.logoThumb) {
          el.logoThumb.innerHTML = '';
          const image = document.createElement('img');
          image.src = state.logoDataUrl;
          image.alt = '';
          el.logoThumb.appendChild(image);
        }
        if (el.logoName) el.logoName.textContent = file.name;
        if (el.logoSelected) el.logoSelected.hidden = false;
        if (el.logoSizeWrap) el.logoSizeWrap.hidden = false;
        if (el.logoSizeValue && el.logoSize) el.logoSizeValue.textContent = `${el.logoSize.value}%`;
        updateMaker();
      };
      reader.onerror = () => {
        window.alert('That logo could not be read. Please try another file.');
        clearLogo();
      };
      reader.readAsDataURL(file);
    }

    async function createThumbnail(blob) {
      const url = URL.createObjectURL(blob);
      try {
        const image = await loadImage(url);
        const canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 640;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, 640, 640);
        return await canvasToBlob(canvas, 'image/webp', 0.88);
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    function qrFilename(extension = 'png') {
      let source = 'qr-code';
      try {
        const url = new URL(state.destination);
        source = `${url.hostname}${url.pathname === '/' ? '' : `-${url.pathname}`}`;
      } catch {}
      return `ops-qr-${safeSlug(source)}.${extension}`;
    }

    async function saveToMyArtwork({ silent = false } = {}) {
      if (!state.qrValid) throw new Error('Enter a valid destination first.');
      const canvas = await renderCompositeQrCanvas(EXPORT_SIZE_PX);
      const blob = await canvasToBlob(canvas, 'image/png');
      const filename = qrFilename('png');
      const file = new File([blob], filename, { type: 'image/png', lastModified: Date.now() });
      const now = new Date().toISOString();
      const id = uid('qr_art');
      const record = {
        id,
        kind: 'artwork',
        name: filename,
        type: 'image/png',
        size: file.size,
        file,
        originalFile: file,
        thumbnail: await createThumbnail(blob),
        widthPx: EXPORT_SIZE_PX,
        heightPx: EXPORT_SIZE_PX,
        hasTransparency: false,
        intendedWidthMm: state.diecutSizeMm,
        intendedHeightMm: state.diecutSizeMm,
        notes: `QR code for ${state.destination}`,
        tags: ['QR code'],
        createdAt: now,
        updatedAt: now,
        qrSettings: {
          destination: state.destination,
          pattern: state.pattern,
          corner: state.corner,
          qrColour: state.qrColour,
          background: state.background,
          logoSize: Number(el.logoSize?.value) || 20,
        },
      };
      await putArtwork(record);
      window.dispatchEvent(new CustomEvent('ops:artwork-scratchpad-updated', { detail: { action: 'save', id, source: 'qr-maker' } }));
      if (!silent) status(el.actionStatus, 'Saved to My Artwork on this device.', 'success');
      return id;
    }

    async function openAdvancedMaker(kind) {
      if (!state.qrValid) return;
      state.busy = true;
      updateActionAvailability();
      const button = kind === 'sheet' ? el.sheetAdvanced : el.diecutAdvanced;
      const originalLabel = button?.textContent || '';
      if (button) button.textContent = 'Preparing artwork…';
      try {
        const id = await saveToMyArtwork({ silent: true });
        localStorage.setItem('opsPendingArtworkId', id);
        const route = kind === 'sheet'
          ? (config.sheetBuilderUrl || '/pages/sticker-sheet-maker')
          : (config.diecutBuilderUrl || '/pages/die-cut-sticker-maker');
        const url = new URL(route, window.location.origin);
        url.searchParams.set('opsArtwork', id);
        url.searchParams.set('opsQr', '1');
        url.searchParams.set('opsQrSize', String(kind === 'sheet' ? state.sheetSizeMm : state.diecutSizeMm));
        if (kind === 'diecut') url.searchParams.set('opsQrShape', state.diecutShape);
        if (kind === 'sheet') {
          url.searchParams.set('opsQrSpacing', String(SHEET_SPACING_MM));
          url.searchParams.set('opsQrMargin', String(SHEET_MARGIN_MM));
        }
        window.location.href = url.toString();
      } catch (error) {
        status(kind === 'sheet' ? el.sheetStatus : el.diecutStatus, error.message || 'The QR could not be passed to the full maker.', 'error');
        state.busy = false;
        if (button) button.textContent = originalLabel;
        updateActionAvailability();
      }
    }

    function protectedWorkerUrl(value, prefix) {
      let parsed;
      let worker;
      try {
        parsed = new URL(value);
        worker = new URL(state.workerUrl);
      } catch {
        throw new Error('The artwork service returned an invalid protected link. Nothing was added to the basket.');
      }
      if (parsed.origin !== worker.origin || !parsed.pathname.startsWith(prefix) || !parsed.searchParams.get('token')) {
        throw new Error('The artwork service returned an unexpected artwork link. Nothing was added to the basket.');
      }
      return parsed.href;
    }

    async function uploadWorkerFile({ file, fileRole, sessionId, product, variantTitle, previewType, selectedOptions, stickerSheetLayout = null, dieCutDetails = null, estimatedDpi = null, extra = {} }) {
      if (!state.workerUrl) throw new Error('The artwork upload service has not been configured.');
      if (!(file instanceof Blob)) throw new Error('A production file could not be prepared.');
      const bytes = await file.arrayBuffer();
      const name = String(file.name || `${fileRole}.png`).replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_').slice(0, 160) || `${fileRole}.png`;
      const materialised = new File([bytes], name, { type: file.type || 'application/octet-stream', lastModified: Date.now() });

      const send = async () => {
        const form = new FormData();
        form.append('file', materialised, materialised.name);
        form.append('sessionId', sessionId);
        form.append('fileRole', fileRole);
        form.append('productTitle', product?.title || 'QR stickers');
        form.append('productHandle', product?.handle || '');
        form.append('variantTitle', variantTitle || '');
        form.append('previewType', previewType);
        form.append('selectedOptions', JSON.stringify(selectedOptions || []));
        if (stickerSheetLayout) form.append('stickerSheetLayout', JSON.stringify(stickerSheetLayout));
        if (dieCutDetails) form.append('dieCutDetails', JSON.stringify(dieCutDetails));
        if (estimatedDpi !== null) form.append('estimatedDpi', String(estimatedDpi));
        form.append('artworkNotes', `QR destination: ${state.destination}`);
        Object.entries(extra || {}).forEach(([key, value]) => form.append(key, String(value)));
        const response = await fetch(`${state.workerUrl}/upload`, { method: 'POST', body: form });
        const text = await response.text();
        let result = {};
        try { result = text ? JSON.parse(text) : {}; } catch { result = { error: text }; }
        if (!response.ok || result?.ok !== true || result?.error) throw new Error(result?.error || result?.message || `Could not upload ${materialised.name}.`);
        if (result.sessionId && result.sessionId !== sessionId) throw new Error('The artwork service returned the wrong upload session. Nothing was added to the basket.');
        return result;
      };

      try { return await send(); }
      catch (firstError) {
        await delay(100);
        try { return await send(); }
        catch (secondError) { throw new Error(secondError?.message || firstError?.message || `Could not upload ${materialised.name}.`); }
      }
    }

    async function addJsonItemsToCart(items) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const text = await response.text();
      let result = {};
      try { result = text ? JSON.parse(text) : {}; } catch { result = { description: text }; }
      if (!response.ok || result?.status) throw new Error(result?.description || result?.message || 'The stickers could not be added to the basket.');
      const returnedItems = Array.isArray(result?.items) ? result.items : [];
      returnedItems.forEach((item) => {
        const props = item?.properties || {};
        if (!props['_Artwork files'] || !props['_Artwork preview']) throw new Error('Shopify did not retain the artwork references. Nothing should be printed from this cart line.');
      });
      return result;
    }

    async function addSheetToCart() {
      if (!state.qrValid || state.contrast < 3) {
        status(el.sheetStatus, 'Increase QR contrast and check the destination before ordering.', 'error');
        return;
      }
      if (!el.sheetProof?.checked) {
        status(el.sheetStatus, 'Confirm that you have checked the destination and sheet preview.', 'error');
        return;
      }
      const variant = selectedSheetVariant();
      if (!variant) {
        status(el.sheetStatus, 'No matching sticker-sheet product option is available.', 'error');
        return;
      }

      state.busy = true;
      updateActionAvailability();
      setBusy(el.sheetAdd, true, 'Saving artwork securely…');
      status(el.sheetStatus, 'Preparing your A4 QR sticker sheet…');
      const sessionId = uid('qr_sheet');
      try {
        const layout = calculateSheetLayout();
        const qrCanvas = await renderCompositeQrCanvas(EXPORT_SIZE_PX);
        const qrBlob = await canvasToBlob(qrCanvas, 'image/png');
        const qrFile = new File([qrBlob], qrFilename('png'), { type: 'image/png', lastModified: Date.now() });
        const finish = sheetFinish(variant);
        const selectedOptions = [
          { name: 'Print finish', value: finish },
          { name: 'A4 print sheets', value: String(state.sheetQuantity) },
          { name: 'QR stickers per sheet', value: String(layout.count) },
          { name: 'Sticker size', value: `${round(layout.size, 1)} mm square` },
        ];
        const layoutData = {
          builderVersion: 'QR-1.0',
          mode: 'repeat',
          sheetWidthMm: A4_WIDTH_MM,
          sheetHeightMm: A4_HEIGHT_MM,
          stickerSizeMm: round(layout.size, 2),
          stickerWidthMm: round(layout.size, 2),
          stickerHeightMm: round(layout.size, 2),
          safeMarginMm: SHEET_MARGIN_MM,
          spacingMm: SHEET_SPACING_MM,
          columns: layout.columns,
          rows: layout.rows,
          estimatedQuantity: layout.count,
          designCount: 1,
          shape: 'square',
          qrDestination: state.destination,
        };

        status(el.sheetStatus, 'Uploading the QR artwork…');
        await uploadWorkerFile({ file: qrFile, fileRole: 'original', sessionId, product: state.sheetProduct, variantTitle: variant.title, previewType: 'qr-sticker-sheet-maker', selectedOptions, stickerSheetLayout: layoutData });

        status(el.sheetStatus, 'Creating the print sheet…');
        const [{ canvas: printCanvas }, previewCanvas] = await Promise.all([createSheetCanvas(), createSheetPreviewCanvas()]);
        const [printBlob, previewBlob, pdfBlob] = await Promise.all([
          canvasToBlob(printCanvas, 'image/png'),
          canvasToBlob(previewCanvas, 'image/png'),
          createPdfBlob(printCanvas),
        ]);
        const cutBlob = new Blob([createSheetCutSvg(layout)], { type: 'image/svg+xml' });
        const projectBlob = new Blob([JSON.stringify({ version: 'QR-1.0', destination: state.destination, qr: { pattern: state.pattern, corner: state.corner, qrColour: state.qrColour, background: state.background, logoSize: Number(el.logoSize?.value) || 20 }, layout: layoutData, createdAt: new Date().toISOString() }, null, 2)], { type: 'application/json' });
        const uploads = [
          [new File([previewBlob], 'customer-preview.png', { type: 'image/png' }), 'customer-preview'],
          [new File([printBlob], 'sticker-sheet-output.png', { type: 'image/png' }), 'sheet-output'],
          [new File([pdfBlob], 'sticker-sheet-output.pdf', { type: 'application/pdf' }), 'sheet-pdf'],
          [new File([cutBlob], 'sticker-sheet-cut-path.svg', { type: 'image/svg+xml' }), 'cut-path'],
          [new File([projectBlob], 'qr-sticker-sheet-project.json', { type: 'application/json' }), 'project-data'],
        ];
        let finalResult = null;
        for (const [file, role] of uploads) {
          status(el.sheetStatus, `Uploading ${file.name}…`);
          finalResult = await uploadWorkerFile({ file, fileRole: role, sessionId, product: state.sheetProduct, variantTitle: variant.title, previewType: 'qr-sticker-sheet-maker', selectedOptions, stickerSheetLayout: layoutData });
        }
        const zipUrl = protectedWorkerUrl(finalResult?.zipUrl, '/zip/');
        const previewUrl = protectedWorkerUrl(finalResult?.previewUrl, '/preview/');
        const discount = activeDiscount(state.discountTiers, state.sheetQuantity);
        const properties = {
          '_Sticker sheet layout': `${layout.count} QR stickers · ${round(layout.size, 1)} mm · ${layout.columns} columns × ${layout.rows} rows`,
          'Sticker size': `${round(layout.size, 1)} mm square`,
          'Sheet arrangement': `${layout.columns} columns × ${layout.rows} rows`,
          'Approximate stickers per sheet': String(layout.count),
          '_Artwork files': zipUrl,
          '_Artwork preview': previewUrl,
          'Artwork notes': `QR destination: ${state.destination}`,
          '_Sheet size': 'A4 · 210 × 297 mm',
          'Print sheets': String(state.sheetQuantity),
          '_Configured saving': discount.percent ? `${discount.percent}%` : '',
          '_Artwork filenames': qrFilename('png'),
          '_upload_session_id': sessionId,
          '_upload_reference': finalResult?.uploadReference || '',
          '_builder_version': 'QR-STICKER-1.1',
        };
        Object.keys(properties).forEach((key) => { if (properties[key] === '') delete properties[key]; });
        status(el.sheetStatus, 'Adding your QR sticker sheet to the basket…');
        await addJsonItemsToCart([{ id: Number(variant.id), quantity: state.sheetQuantity, properties }]);
        status(el.sheetStatus, 'Sticker sheet added. Opening your basket…', 'success');
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (error) {
        console.error(error);
        status(el.sheetStatus, error.message || 'The QR sticker sheet could not be saved. Nothing was added to the basket.', 'error');
      } finally {
        state.busy = false;
        setBusy(el.sheetAdd, false);
        updateActionAvailability();
      }
    }

    function diecutSelectedOptions(plan) {
      return [
        { name: 'Size', value: `${diecutSizeCategory()} · ${state.diecutSizeMm} mm` },
        { name: 'Shape', value: diecutRounded() ? 'Rounded corners' : 'Square corners' },
        { name: 'Finish', value: el.diecutFinish?.value || '' },
        { name: 'Sticker quantity', value: String(state.diecutQuantity) },
        ...(state.diecutUsesPackVariants ? [{ name: 'Bundle breakdown', value: diecutPlanLabel(plan) }] : []),
        { name: 'QR destination', value: state.destination },
      ];
    }

    async function addDiecutToCart() {
      if (!state.qrValid || state.contrast < 3) {
        status(el.diecutStatus, 'Increase QR contrast and check the destination before ordering.', 'error');
        return;
      }
      if (!el.diecutProof?.checked) {
        status(el.diecutStatus, 'Confirm that you have checked the destination and sticker preview.', 'error');
        return;
      }
      const plan = buildDiecutPlan(state.diecutQuantity);
      if (!plan?.items?.length) {
        status(el.diecutStatus, 'No matching sticker option is available for that size, finish and quantity.', 'error');
        return;
      }

      state.busy = true;
      updateActionAvailability();
      setBusy(el.diecutAdd, true, 'Saving artwork securely…');
      const sessionId = uid('qr_diecut');
      try {
        const qrCanvas = await renderCompositeQrCanvas(EXPORT_SIZE_PX);
        const qrBlob = await canvasToBlob(qrCanvas, 'image/png');
        const qrFile = new File([qrBlob], qrFilename('png'), { type: 'image/png', lastModified: Date.now() });
        const sizeMm = state.diecutSizeMm;
        const dpi = Math.round(EXPORT_SIZE_PX / (sizeMm / 25.4));
        const selectedOptions = diecutSelectedOptions(plan);
        const variantTitle = `${diecutSizeCategory()} / ${el.diecutFinish?.value || ''} / ${state.diecutQuantity} stickers`;
        const dieCutDetails = {
          shape: diecutRounded() ? 'rounded-square' : 'square',
          cornerRadiusMm: diecutCornerRadiusMm(sizeMm),
          qrInsetMm: diecutQrInsetMm(sizeMm),
          longestEdgeMm: sizeMm,
          finishedWidthMm: sizeMm,
          finishedHeightMm: sizeMm,
          borderMm: 0,
          smoothingMm: 0,
          bleedMm: DIECUT_BLEED_MM,
          qrDestination: state.destination,
        };

        status(el.diecutStatus, 'Uploading the QR artwork…');
        await uploadWorkerFile({ file: qrFile, fileRole: 'original', sessionId, product: state.diecutProduct, variantTitle, previewType: 'qr-die-cut-sticker-maker', selectedOptions, dieCutDetails, estimatedDpi: dpi });

        status(el.diecutStatus, 'Creating the 300 DPI sticker file…');
        const printCanvas = await createDiecutPrintCanvas();
        const printBlob = await canvasToBlob(printCanvas, 'image/png');
        await uploadWorkerFile({
          file: new File([printBlob], 'qr-sticker-print-300dpi.png', { type: 'image/png' }),
          fileRole: 'production-output',
          sessionId,
          product: state.diecutProduct,
          variantTitle,
          previewType: 'qr-die-cut-sticker-maker',
          selectedOptions,
          dieCutDetails,
          estimatedDpi: dpi,
          extra: { outputDpi: SHEET_DPI, outputWidthPx: printCanvas.width, outputHeightPx: printCanvas.height },
        });

        const cutBlob = new Blob([createDiecutCutSvg()], { type: 'image/svg+xml' });
        await uploadWorkerFile({ file: new File([cutBlob], 'qr-sticker-cut-path.svg', { type: 'image/svg+xml' }), fileRole: 'cut-path', sessionId, product: state.diecutProduct, variantTitle, previewType: 'qr-die-cut-sticker-maker', selectedOptions, dieCutDetails, estimatedDpi: dpi });

        const project = {
          version: 'QR-STICKER-1.1',
          destination: state.destination,
          productHandle: state.diecutProduct?.handle || config.diecutProductHandle || 'custom-stickers',
          orderQuantity: state.diecutQuantity,
          orderPlan: plan.items.map((item) => ({ variantId: item.variant.id, variantTitle: item.variant.title, unitsPerPack: item.units, packs: item.packs })),
          maker: { sizeMm, shape: state.diecutShape, cornerRadiusMm: diecutCornerRadiusMm(sizeMm), qrInsetMm: diecutQrInsetMm(sizeMm), border: false, bleedMm: DIECUT_BLEED_MM },
          qr: { pattern: state.pattern, corner: state.corner, qrColour: state.qrColour, background: state.background, logoSize: Number(el.logoSize?.value) || 20 },
          createdAt: new Date().toISOString(),
        };
        await uploadWorkerFile({ file: new File([JSON.stringify(project, null, 2)], 'project-data.json', { type: 'application/json' }), fileRole: 'project-data', sessionId, product: state.diecutProduct, variantTitle, previewType: 'qr-die-cut-sticker-maker', selectedOptions, dieCutDetails, estimatedDpi: dpi });

        status(el.diecutStatus, 'Creating the final proof…');
        const proofCanvas = await createDiecutProofCanvas();
        const proofBlob = await canvasToBlob(proofCanvas, 'image/png');
        const finalResult = await uploadWorkerFile({ file: new File([proofBlob], 'qr-sticker-full-resolution-proof.png', { type: 'image/png' }), fileRole: 'final-proof', sessionId, product: state.diecutProduct, variantTitle, previewType: 'qr-die-cut-sticker-maker', selectedOptions, dieCutDetails, estimatedDpi: dpi, extra: { outputWidthPx: proofCanvas.width, outputHeightPx: proofCanvas.height } });
        const zipUrl = protectedWorkerUrl(finalResult?.zipUrl, '/zip/');
        const previewUrl = protectedWorkerUrl(finalResult?.previewUrl, '/preview/');

        let estimatedTotal = Number(plan.total || 0);
        const discount = state.diecutUsesPackVariants ? { percent: 0, quantity: 0 } : activeDiscount(state.discountTiers, state.diecutQuantity);
        if (discount.percent) estimatedTotal -= Math.round(estimatedTotal * discount.percent / 100);
        const properties = {
          'Artwork reference': `OPS-QR-${Date.now().toString(36).toUpperCase()}`,
          '_Artwork files': zipUrl,
          '_Artwork preview': previewUrl,
          '_Builder preset': 'qr-sticker-maker',
          '_Finished size': `${sizeMm.toFixed(1)} × ${sizeMm.toFixed(1)} mm`,
          '_Shape': diecutRounded() ? 'Rounded-corner QR sticker' : 'Square QR sticker',
          '_Estimated DPI': String(dpi),
          '_Sticker quantity': String(state.diecutQuantity),
          '_Pack breakdown': state.diecutUsesPackVariants ? diecutPlanLabel(plan) : '',
          '_Estimated order total': formatMoney(estimatedTotal, config.currency),
          '_Estimated unit price': formatUnitPrice(estimatedTotal / state.diecutQuantity, 'each', config.currency),
          'QR destination': state.destination,
          '_upload_session_id': sessionId,
          '_upload_reference': finalResult?.uploadReference || '',
          '_builder_version': 'QR-STICKER-1.1',
        };
        Object.keys(properties).forEach((key) => { if (properties[key] === '') delete properties[key]; });
        const items = plan.items.map((item) => ({ id: Number(item.variant.id), quantity: Math.max(1, Number(item.packs) || 1), properties }));
        status(el.diecutStatus, 'Adding your QR stickers to the basket…');
        await addJsonItemsToCart(items);
        status(el.diecutStatus, 'QR stickers added. Opening your basket…', 'success');
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (error) {
        console.error(error);
        status(el.diecutStatus, error.message || 'The QR stickers could not be saved. Nothing was added to the basket.', 'error');
      } finally {
        state.busy = false;
        setBusy(el.diecutAdd, false);
        updateActionAvailability();
      }
    }

    async function downloadPng() {
      state.busy = true;
      updateActionAvailability();
      setBusy(el.downloadPng, true, 'Creating PNG…');
      status(el.actionStatus, 'Creating a high-resolution QR PNG…');
      try {
        const canvas = await renderCompositeQrCanvas(EXPORT_SIZE_PX);
        const blob = await canvasToBlob(canvas, 'image/png');
        downloadBlob(blob, qrFilename('png'));
        status(el.actionStatus, 'PNG downloaded.', 'success');
      } catch (error) {
        status(el.actionStatus, error.message || 'The PNG could not be created.', 'error');
      } finally {
        state.busy = false;
        setBusy(el.downloadPng, false);
        updateActionAvailability();
      }
    }

    async function downloadSvg() {
      state.busy = true;
      updateActionAvailability();
      setBusy(el.downloadSvg, true, 'Creating SVG…');
      status(el.actionStatus, 'Creating the vector QR file…');
      try {
        const blob = await renderQrSvgBlob();
        downloadBlob(blob, qrFilename('svg'));
        status(el.actionStatus, 'SVG downloaded.', 'success');
      } catch (error) {
        status(el.actionStatus, error.message || 'The SVG could not be created.', 'error');
      } finally {
        state.busy = false;
        setBusy(el.downloadSvg, false);
        updateActionAvailability();
      }
    }

    async function saveArtworkAction() {
      state.busy = true;
      updateActionAvailability();
      setBusy(el.saveArtwork, true, 'Saving…');
      status(el.actionStatus, 'Saving the finished QR to My Artwork…');
      try { await saveToMyArtwork(); }
      catch (error) { status(el.actionStatus, error.message || 'The QR could not be saved.', 'error'); }
      finally {
        state.busy = false;
        setBusy(el.saveArtwork, false);
        updateActionAvailability();
      }
    }

    async function downloadSheetPng() {
      state.busy = true;
      updateActionAvailability();
      setBusy(el.sheetDownload, true, 'Creating A4 PNG…');
      status(el.sheetStatus, 'Creating the 300 DPI A4 sheet…');
      try {
        const { canvas } = await createSheetCanvas();
        const blob = await canvasToBlob(canvas, 'image/png');
        downloadBlob(blob, 'ops-qr-sticker-sheet-a4.png');
        status(el.sheetStatus, 'A4 sheet PNG downloaded.', 'success');
      } catch (error) {
        status(el.sheetStatus, error.message || 'The A4 sheet could not be created.', 'error');
      } finally {
        state.busy = false;
        setBusy(el.sheetDownload, false);
        updateActionAvailability();
      }
    }

    el.destination.addEventListener('input', updateMaker);
    el.destination.addEventListener('change', updateMaker);
    el.destination.addEventListener('blur', updateMaker);
    el.logoInput?.addEventListener('change', handleLogoUpload);
    el.logoRemove?.addEventListener('click', clearLogo);
    el.logoSize?.addEventListener('input', () => {
      if (el.logoSizeValue) el.logoSizeValue.textContent = `${el.logoSize.value}%`;
      updateMaker();
    });

    el.patternButtons.forEach((button) => button.addEventListener('click', () => {
      state.pattern = button.dataset.opsQrPattern || 'square';
      setPressedButton(el.patternButtons, button);
      updateMaker();
    }));
    el.cornerButtons.forEach((button) => button.addEventListener('click', () => {
      state.corner = button.dataset.opsQrCorner || 'square';
      setPressedButton(el.cornerButtons, button);
      updateMaker();
    }));

    bindColourControl(el.qrColour, el.qrColourHex, (colour) => { state.qrColour = colour; });
    bindColourControl(el.background, el.backgroundHex, (colour) => { state.background = colour; });

    el.downloadPng?.addEventListener('click', downloadPng);
    el.downloadSvg?.addEventListener('click', downloadSvg);
    el.saveArtwork?.addEventListener('click', saveArtworkAction);

    el.sheetSize?.addEventListener('input', () => {
      state.sheetSizeMm = clamp(Number(el.sheetSize.value) || 36, 20, 60);
      if (el.sheetSizeValue) el.sheetSizeValue.textContent = `${Math.round(state.sheetSizeMm)} mm`;
      const layout = calculateSheetLayout();
      if (el.sheetSummary) el.sheetSummary.textContent = `${layout.columns} across × ${layout.rows} rows · ${layout.count} stickers per A4 sheet`;
      drawSheetPreview().catch(() => {});
    });
    el.sheetFinish?.addEventListener('change', updateSheetPricing);
    el.sheetQuantity?.addEventListener('input', updateSheetPricing);
    el.sheetQuantity?.addEventListener('change', updateSheetPricing);
    el.sheetMinus?.addEventListener('click', () => { if (el.sheetQuantity) el.sheetQuantity.value = String(Math.max(1, state.sheetQuantity - 1)); updateSheetPricing(); });
    el.sheetPlus?.addEventListener('click', () => { if (el.sheetQuantity) el.sheetQuantity.value = String(Math.min(1000, state.sheetQuantity + 1)); updateSheetPricing(); });
    el.sheetPresets?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity]');
      if (!button || !el.sheetQuantity) return;
      el.sheetQuantity.value = button.dataset.quantity;
      updateSheetPricing();
    });
    el.sheetProof?.addEventListener('change', updateActionAvailability);
    el.sheetAdd?.addEventListener('click', addSheetToCart);
    el.sheetDownload?.addEventListener('click', downloadSheetPng);
    el.sheetAdvanced?.addEventListener('click', () => openAdvancedMaker('sheet'));

    el.diecutSize?.addEventListener('input', () => {
      state.diecutSizeMm = clamp(Math.round(Number(el.diecutSize.value) || 50), 20, 100);
      if (el.diecutSizeValue) el.diecutSizeValue.textContent = `${state.diecutSizeMm} mm`;
      updateDiecutPricing();
    });
    el.diecutSizePresets?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size]');
      if (!button || !el.diecutSize) return;
      state.diecutSizeMm = clamp(Number(button.dataset.size) || 50, 20, 100);
      el.diecutSize.value = String(state.diecutSizeMm);
      if (el.diecutSizeValue) el.diecutSizeValue.textContent = `${state.diecutSizeMm} mm`;
      updateDiecutPricing();
    });
    el.diecutShape?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-shape]');
      if (!button) return;
      state.diecutShape = button.dataset.shape === 'rounded' ? 'rounded' : 'square';
      el.diecutShape.querySelectorAll('[data-shape]').forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (el.diecutShapeValue) el.diecutShapeValue.textContent = diecutRounded() ? 'Rounded' : 'Square';
      if (state.qrValid) updateDerivedPreviews(state.previewRevision).catch(() => {});
      updateDiecutPricing();
    });
    el.diecutFinish?.addEventListener('change', updateDiecutPricing);
    el.diecutQuantity?.addEventListener('input', updateDiecutPricing);
    el.diecutQuantity?.addEventListener('change', updateDiecutPricing);
    el.diecutMinus?.addEventListener('click', () => { if (el.diecutQuantity) el.diecutQuantity.value = String(Math.max(1, state.diecutQuantity - 1)); updateDiecutPricing(); });
    el.diecutPlus?.addEventListener('click', () => { if (el.diecutQuantity) el.diecutQuantity.value = String(Math.min(1000, state.diecutQuantity + 1)); updateDiecutPricing(); });
    el.diecutPresets?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity]');
      if (!button || !el.diecutQuantity) return;
      el.diecutQuantity.value = button.dataset.quantity;
      updateDiecutPricing();
    });
    el.diecutProof?.addEventListener('change', updateActionAvailability);
    el.diecutAdd?.addEventListener('click', addDiecutToCart);
    el.diecutAdvanced?.addEventListener('click', () => openAdvancedMaker('diecut'));

    const sheetLayout = calculateSheetLayout();
    if (el.sheetSummary) el.sheetSummary.textContent = `${sheetLayout.columns} across × ${sheetLayout.rows} rows · ${sheetLayout.count} stickers per A4 sheet`;
    populateSheetFinish();
    setupDiecutProduct();
    updateSafety();
    updateMaker();
    updateSheetPricing();
    updateDiecutPricing();
    requestAnimationFrame(updateMaker);
    setTimeout(updateMaker, 140);
  }

  function initAllQrMakers() {
    document.querySelectorAll('.ops-qr-maker').forEach(initQrMaker);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAllQrMakers);
  else initAllQrMakers();

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches?.('.ops-qr-maker') ? event.target : event.target.querySelector('.ops-qr-maker');
    if (!root) return;
    root.removeAttribute('data-ops-qr-initialised');
    initQrMaker(root);
  });
})();
